// SPDX-License-Identifier: MIT
//
// Random picker for the "thinking" stage animation shown in the chat
// assistant fallback bubble. The pool is a small, themable set of inline
// SVG icons rendered with CSS keyframe animations — same visual idiom as a
// short looping GIF, but ships inline with the repo (no external assets, no
// licensing overhead, no network fetch, and respects the active theme).
//
// The picker is deterministic per render *session* (seeded by the run's
// `startedAt`) so the same run shows the same animation across re-renders
// while it is still in the waiting state. A new run (new `startedAt`) gets a
// fresh pick.

import { html, type TemplateResult } from "lit";
import { icons } from "../icons.ts";

export const THINKING_ICON_IDS = ["brain", "loader", "helpCircle", "zap"] as const;

export type ThinkingIconId = (typeof THINKING_ICON_IDS)[number];

/** 32-bit FNV-1a hash. Stable across runs, no crypto needed. */
function hashSeed(seed: number): number {
  let h = 0x811c9dc5;
  const bytes = new Uint8Array(4);
  new DataView(bytes.buffer).setUint32(0, seed >>> 0, true);
  for (const b of bytes) {
    h ^= b;
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * Pick a stable icon id for the given run.
 * `seed` is typically the run's `startedAt` timestamp; passing the same seed
 * always returns the same id, so a fresh "Thinking…" render session shows a
 * consistent animation while it lingers.
 */
export function pickThinkingIcon(seed: number): ThinkingIconId {
  const idx = hashSeed(seed) % THINKING_ICON_IDS.length;
  return THINKING_ICON_IDS[idx]!;
}

/** Render the chosen thinking icon as a small inline SVG with a class hook. */
export function renderThinkingIcon(id: ThinkingIconId): TemplateResult {
  // The icons module is Record<string, TemplateResult>; we filter by allowlist.
  const src = icons[id as unknown as keyof typeof icons];
  return html`<span class="chat-thinking-gif chat-thinking-gif--${id}" aria-hidden="true"
    >${src}</span
  >`;
}
